import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeContext = createContext();
const THEME_PREFERENCE_KEY = 'theme-preference';

const getSystemTheme = () => {
    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
        return 'light';
    }
};

// Every site opens LIGHT unless the visitor picked dark with the theme toggle
// (client request 2026-10-24). It used to follow the device ("system"), so a
// phone in dark mode got the dark site without asking. 'system' was only ever
// the stored default — no control on the site chooses it — so a stored
// 'system' is read as light too.
const getStoredThemePreference = () => {
    try {
        const storedPreference = localStorage.getItem(THEME_PREFERENCE_KEY);
        if (storedPreference === 'light' || storedPreference === 'dark') return storedPreference;
        if (storedPreference === 'system') return 'light';

        // Backward compatibility with the previous single-key implementation.
        const legacyTheme = localStorage.getItem('theme');
        if (legacyTheme === 'light' || legacyTheme === 'dark') return legacyTheme;
    } catch {}

    return 'light';
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [themePreference, setThemePreference] = useState(getStoredThemePreference);
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    const theme = themePreference === 'system' ? systemTheme : themePreference;

    useEffect(() => {
        const root = window.document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        root.style.colorScheme = theme;

        try {
            localStorage.setItem(THEME_PREFERENCE_KEY, themePreference);
            localStorage.setItem('theme', theme);
        } catch {}
    }, [theme, themePreference]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            setSystemTheme(e.matches ? 'dark' : 'light');
        };

        setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === THEME_PREFERENCE_KEY || e.key === 'theme') {
                setThemePreference(getStoredThemePreference());
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemePreference(prev => {
            const currentTheme = prev === 'system' ? getSystemTheme() : prev;
            return currentTheme === 'light' ? 'dark' : 'light';
        });
    }, []);

    const resetThemeToSystem = useCallback(() => {
        setThemePreference('system');
    }, []);

    // Memoised so an App re-render (every navigation) doesn't re-render every
    // theme consumer.
    const themeValue = useMemo(() => ({
        theme,
        themePreference,
        toggleTheme,
        resetThemeToSystem,
        isDark: theme === 'dark',
    }), [theme, themePreference, toggleTheme, resetThemeToSystem]);

    return (
        <ThemeContext.Provider value={themeValue}>
            {children}
        </ThemeContext.Provider>
    );
};
